/**
 * RESOLVEDOR ÚNICO DE IMAGENS PARA O PDF.
 *
 * O pdfmake desenha **apenas PNG e JPEG**. Qualquer outra coisa entregue em
 * `image:` derruba a geração inteira com
 *
 *     Invalid image: Unknown image format.
 *     Images dictionary should contain dataURL entries
 *
 * e, como a geração é assíncrona, isso chegava à interface como
 * `Unhandled Promise Rejection` — sem dizer qual imagem, de qual documento,
 * de qual campo.
 *
 * O buraco estava nos guardas espalhados pelo código:
 *
 *   - `isEmbeddableImage` aceitava `gif` e `webp`, que o pdfmake recusa;
 *   - `startsWith("data:image")` aceita literalmente qualquer coisa,
 *     inclusive SVG, que precisa ir em `svg:` e não em `image:`.
 *
 * Aqui tudo passa por um lugar só. Nada entra no DocumentDefinition sem ter
 * sido classificado, e uma imagem inválida vira um resultado explícito — com
 * o motivo — em vez de uma exceção no meio da montagem.
 *
 * Este módulo é PURO e síncrono: não busca arquivo, não faz rede, não
 * converte formato. Converter WEBP exigiria canvas (navegador) e não existe
 * no servidor; a decisão consciente é **recusar de forma identificável** e
 * mandar corrigir na origem, em vez de silenciar a imagem.
 */

export type PdfImage =
  /** Pronta para `image:` — PNG ou JPEG em dataURL. */
  | { readonly kind: "raster"; readonly dataUrl: string; readonly mime: "image/png" | "image/jpeg" }
  /** Vetorial: vai em `svg:`, nunca em `image:`. */
  | { readonly kind: "svg"; readonly svg: string }
  /** Ausente — campo opcional não preenchido. Não é erro. */
  | { readonly kind: "empty" }
  /** Existe algo, mas não dá para desenhar. `reason` é para o usuário; `detail` para o log. */
  | { readonly kind: "invalid"; readonly reason: string; readonly detail: string };

const DATA_URL = /^data:(image\/[a-z0-9.+-]+);base64,([A-Za-z0-9+/=\s]+)$/i;

/** Assinaturas binárias, lidas do início do base64 — não do rótulo, que mente. */
const BASE64_SIGNATURES: ReadonlyArray<{ prefix: string; mime: "image/png" | "image/jpeg" }> = [
  { prefix: "iVBORw0KGgo", mime: "image/png" },   // \x89PNG
  { prefix: "/9j/", mime: "image/jpeg" }           // \xFF\xD8\xFF
];
const UNSUPPORTED_SIGNATURES: ReadonlyArray<{ prefix: string; label: string }> = [
  { prefix: "R0lGOD", label: "GIF" },              // GIF8
  { prefix: "UklGR", label: "WEBP" },              // RIFF....WEBP
  { prefix: "Qk", label: "BMP" },                  // BM
  { prefix: "SUkq", label: "TIFF" },
  { prefix: "TU0A", label: "TIFF" },
  { prefix: "JVBERi0", label: "PDF" }              // %PDF — anexo trocado por imagem
];

const SUPPORTED_MIME: Readonly<Record<string, "image/png" | "image/jpeg">> = {
  "image/png": "image/png",
  "image/jpeg": "image/jpeg",
  "image/jpg": "image/jpeg"
};

const clean = (value: string) => value.replace(/\s+/g, "");

/**
 * Base64 bem formado? Um payload corrompido quebra o pdfmake tão bem quanto
 * um GIF.
 *
 * O critério é padding e alfabeto — não comprimento. Um piso de tamanho
 * pareceria prudente e rejeitaria imagens pequenas legítimas (um ícone de 1×1
 * cabe em poucos bytes), sem detectar nada que o padding já não detecte. O
 * mínimo de 8 existe só para haver bytes suficientes para ler a assinatura.
 */
function isWellFormedBase64(payload: string): boolean {
  if (payload.length < 8 || payload.length % 4 !== 0) return false;
  return /^[A-Za-z0-9+/]+={0,2}$/.test(payload);
}

/**
 * Classifica qualquer referência de imagem do sistema. Aceita dataURL,
 * base64 puro legado e SVG em texto; recusa, com motivo, tudo o que o
 * pdfmake não desenha.
 */
export function resolvePdfImage(input: unknown): PdfImage {
  if (input === null || input === undefined) return { kind: "empty" };
  if (typeof input !== "string") {
    return { kind: "invalid", reason: "A imagem está em um formato que o sistema não reconhece.", detail: `tipo ${typeof input}` };
  }

  const value = input.trim();
  if (value === "") return { kind: "empty" };

  // SVG — vetorial. Vai em `svg:`, e passá-lo em `image:` é o erro clássico.
  if (value.startsWith("<svg") || (value.startsWith("<?xml") && value.includes("<svg"))) {
    return { kind: "svg", svg: value };
  }
  if (/^data:image\/svg\+xml/i.test(value)) {
    const base64 = value.match(DATA_URL);
    if (base64?.[2]) {
      try {
        const decoded = typeof atob === "function"
          ? atob(clean(base64[2]))
          : Buffer.from(clean(base64[2]), "base64").toString("utf8");
        if (decoded.includes("<svg")) return { kind: "svg", svg: decoded };
      } catch { /* cai na recusa abaixo */ }
    }
    const comma = value.indexOf(",");
    const inline = comma >= 0 ? decodeURIComponent(value.slice(comma + 1)) : "";
    if (inline.includes("<svg")) return { kind: "svg", svg: inline };
    return { kind: "invalid", reason: "A imagem vetorial (SVG) não pôde ser lida.", detail: "svg dataURL ilegível" };
  }

  // JPEG em base64 puro começa com "/9j/". A checagem de caminho logo abaixo
  // olha a barra inicial, e sem esta saída antecipada toda assinatura ou foto
  // JPEG legada era recusada como se fosse um caminho de arquivo.
  if (value.startsWith("/9j/")) {
    const payload = clean(value);
    if (isWellFormedBase64(payload)) return { kind: "raster", dataUrl: `data:image/jpeg;base64,${payload}`, mime: "image/jpeg" };
    return { kind: "invalid", reason: "O conteúdo da imagem está corrompido ou incompleto.", detail: "base64 JPEG malformado" };
  }

  // Endereços: o pdfmake não busca nada por conta própria no navegador.
  if (/^blob:/i.test(value)) {
    return { kind: "invalid", reason: "A imagem aponta para um endereço temporário do navegador e não pode entrar no documento.", detail: "blob: URL" };
  }
  if (/^https?:\/\//i.test(value)) {
    return { kind: "invalid", reason: "A imagem está em um endereço externo. Ela precisa estar armazenada no sistema para entrar no documento.", detail: "URL remota" };
  }
  if (/^(data:)?[a-z]:[\\/]/i.test(value) || value.startsWith("/") || value.startsWith("./") || value.startsWith("../")) {
    return { kind: "invalid", reason: "A imagem aponta para um caminho de arquivo, que não existe no navegador.", detail: "caminho local" };
  }

  // dataURL declarada.
  const match = value.match(DATA_URL);
  if (match) {
    const declared = (match[1] || "").toLowerCase();
    const payload = clean(match[2] || "");
    if (!isWellFormedBase64(payload)) {
      return { kind: "invalid", reason: "O conteúdo da imagem está corrompido ou incompleto.", detail: `${declared} base64 malformado` };
    }
    // O rótulo pode mentir: o que vale é a assinatura binária.
    const sniffed = sniff(payload);
    if (sniffed.kind === "raster") return { kind: "raster", dataUrl: `data:${sniffed.mime};base64,${payload}`, mime: sniffed.mime };
    if (sniffed.kind === "unsupported") {
      return { kind: "invalid", reason: `Imagem em ${sniffed.label}. O documento aceita apenas PNG e JPEG — converta a imagem e cadastre novamente.`, detail: `declarado ${declared}, conteúdo ${sniffed.label}` };
    }
    const supported = SUPPORTED_MIME[declared];
    if (supported) return { kind: "raster", dataUrl: `data:${supported};base64,${payload}`, mime: supported };
    return { kind: "invalid", reason: `Imagem em formato não suportado (${declared}). O documento aceita apenas PNG e JPEG.`, detail: `mime ${declared}` };
  }

  if (value.startsWith("data:")) {
    return { kind: "invalid", reason: "O conteúdo da imagem está corrompido ou incompleto.", detail: "dataURL malformada" };
  }

  // Base64 puro legado: o formato real vem da assinatura, nunca de um palpite.
  const payload = clean(value);
  if (isWellFormedBase64(payload)) {
    const sniffed = sniff(payload);
    if (sniffed.kind === "raster") return { kind: "raster", dataUrl: `data:${sniffed.mime};base64,${payload}`, mime: sniffed.mime };
    if (sniffed.kind === "unsupported") {
      return { kind: "invalid", reason: `Imagem em ${sniffed.label}. O documento aceita apenas PNG e JPEG — converta a imagem e cadastre novamente.`, detail: `base64 puro, conteúdo ${sniffed.label}` };
    }
    return { kind: "invalid", reason: "Não foi possível identificar o formato da imagem.", detail: "base64 puro sem assinatura conhecida" };
  }

  return { kind: "invalid", reason: "A imagem está em um formato que o sistema não reconhece.", detail: `texto de ${value.length} caractere(s)` };
}

function sniff(payload: string): { kind: "raster"; mime: "image/png" | "image/jpeg" } | { kind: "unsupported"; label: string } | { kind: "unknown" } {
  for (const s of BASE64_SIGNATURES) if (payload.startsWith(s.prefix)) return { kind: "raster", mime: s.mime };
  for (const s of UNSUPPORTED_SIGNATURES) if (payload.startsWith(s.prefix)) return { kind: "unsupported", label: s.label };
  return { kind: "unknown" };
}

/**
 * A dataURL pronta para `image:`, ou `null`.
 *
 * É o atalho para imagem OPCIONAL: quando não dá para desenhar, o bloco é
 * omitido e o resto do documento continua. Para imagem obrigatória (a
 * assinatura de um documento assinado, por exemplo), use `resolvePdfImage` e
 * trate o `invalid` — aí a geração deve parar com uma mensagem clara, não
 * produzir um documento sem a assinatura.
 */
export function pdfImageOrNull(input: unknown): string | null {
  const resolved = resolvePdfImage(input);
  return resolved.kind === "raster" ? resolved.dataUrl : null;
}

/**
 * `true` só para o que o pdfmake realmente desenha.
 *
 * É um type predicate de propósito: quem chama precisa que o TypeScript
 * estreite o valor para `string` no ramo verdadeiro, senão um `null` continua
 * possível exatamente onde o node `image:` é montado.
 */
export function isDrawableByPdfMake(input: unknown): input is string {
  return resolvePdfImage(input).kind === "raster";
}

/**
 * Registro técnico de uma imagem recusada, sem vazar o conteúdo.
 *
 * O base64 completo NUNCA entra no log: são megabytes, e uma assinatura ou
 * foto de campo não tem por que aparecer em console de produção.
 */
export function describeRejectedImage(
  context: { documentType: string; documentId?: string | null; field: string; source?: string },
  input: unknown,
  resolved: Extract<PdfImage, { kind: "invalid" }>
): Record<string, unknown> {
  const raw = typeof input === "string" ? input.trim() : "";
  const mimeMatch = raw.match(/^data:([a-z0-9.+/-]+);/i);
  return {
    event: "PDF_IMAGE_VALIDATION_FAILED",
    documentType: context.documentType,
    documentId: context.documentId ?? null,
    field: context.field,
    source: context.source ?? "desconhecida",
    mime: mimeMatch?.[1] ?? "não declarado",
    payloadLength: raw.length,
    reason: resolved.reason,
    detail: resolved.detail
  };
}
